import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Tag, Plus, Edit2, Trash2, Save, X, Loader2 } from "lucide-react";

const ICON_OPTIONS = [
    "Sparkles", "Heart", "HeartPulse", "Cross", "Flame", "BookOpen", "GraduationCap",
    "Home", "Banknote", "Briefcase", "Baby", "TrendingUp", "Dove", "Flower",
    "HandHeart", "Sunrise", "Users", "Church", "Star", "Shield", "Gift",
];

const emptyForm = {
    slug: "",
    label_en: "",
    label_ta: "",
    label_fr: "",
    icon: "Sparkles",
    description: "",
    sort_order: 999,
    is_active: true,
};

export default function CategoryManagement() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [showForm, setShowForm] = useState(false);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/categories");
            setItems(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const resetForm = () => { setForm(emptyForm); setEditingId(null); setShowForm(false); setError(""); };

    const startEdit = (c) => {
        setEditingId(c.id);
        setForm({
            slug: c.slug || "",
            label_en: c.label_en || "",
            label_ta: c.label_ta || "",
            label_fr: c.label_fr || "",
            icon: c.icon || "Sparkles",
            description: c.description || "",
            sort_order: c.sort_order ?? 999,
            is_active: c.is_active !== false,
        });
        setShowForm(true);
        setError("");
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setError("");
        try {
            if (editingId) {
                await api.put(`/admin/categories/${editingId}`, form);
            } else {
                await api.post("/admin/categories", form);
            }
            await load();
            resetForm();
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    const remove = async (c) => {
        if (c.is_system) return;
        if (!window.confirm(`Delete "${c.label_en}"? Existing intentions in this category will keep their category tag as free text.`)) return;
        try {
            await api.delete(`/admin/categories/${c.id}`);
            await load();
        } catch (err) {
            alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    const toggleActive = async (c) => {
        try {
            await api.put(`/admin/categories/${c.id}`, {
                slug: c.slug,
                label_en: c.label_en,
                label_ta: c.label_ta,
                label_fr: c.label_fr,
                icon: c.icon,
                description: c.description,
                sort_order: c.sort_order,
                is_active: !c.is_active,
            });
            await load();
        } catch (err) {
            alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="p-card p-6" data-testid="admin-category-management">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Tag size={16} style={{ color: "#7F1D1D" }} />
                    <div className="font-display text-2xl" style={{ color: "#1E1B4B" }}>Intention Categories</div>
                </div>
                <button
                    onClick={() => { if (showForm && !editingId) { resetForm(); } else { setForm(emptyForm); setEditingId(null); setShowForm(true); setError(""); } }}
                    className="btn-primary text-xs inline-flex items-center gap-1.5"
                    data-testid="admin-category-new-button"
                >
                    {showForm && !editingId ? <><X size={14} /> Cancel</> : <><Plus size={14} /> New category</>}
                </button>
            </div>

            {showForm && (
                <form onSubmit={submit} className="mt-5 grid gap-3" data-testid="admin-category-form">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>Label (English) *</label>
                            <input required value={form.label_en} onChange={(e) => setForm({ ...form, label_en: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-full border"
                                style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                data-testid="admin-category-label-en" />
                        </div>
                        <div>
                            <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>Slug (optional)</label>
                            <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}
                                placeholder="auto-generated from label"
                                className="w-full px-4 py-2.5 rounded-full border"
                                style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                data-testid="admin-category-slug" />
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>Label (தமிழ்)</label>
                            <input value={form.label_ta} onChange={(e) => setForm({ ...form, label_ta: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-full border"
                                style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                data-testid="admin-category-label-ta" />
                        </div>
                        <div>
                            <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>Label (Français)</label>
                            <input value={form.label_fr} onChange={(e) => setForm({ ...form, label_fr: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-full border"
                                style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                data-testid="admin-category-label-fr" />
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                        <div>
                            <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>Icon</label>
                            <select value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-full border"
                                style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                data-testid="admin-category-icon">
                                {ICON_OPTIONS.map((i) => (<option key={i} value={i}>{i}</option>))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>Sort order</label>
                            <input type="number" value={form.sort_order}
                                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || "999", 10) })}
                                className="w-full px-4 py-2.5 rounded-full border"
                                style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                data-testid="admin-category-sort" />
                        </div>
                        <div className="flex items-end">
                            <label className="text-sm flex items-center gap-2" style={{ color: "#1E1B4B" }}>
                                <input type="checkbox" checked={form.is_active}
                                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                                    data-testid="admin-category-active" />
                                Active (visible to users)
                            </label>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>Description (optional)</label>
                        <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                            className="w-full px-4 py-2.5 rounded-full border"
                            style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                            data-testid="admin-category-description" />
                    </div>
                    {error && <div className="text-sm" style={{ color: "#7F1D1D" }} data-testid="admin-category-error">{error}</div>}
                    <div className="flex gap-2">
                        <button type="submit" disabled={busy} className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="admin-category-submit">
                            {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                            {editingId ? "Save changes" : "Create category"}
                        </button>
                        <button type="button" onClick={resetForm} className="text-xs px-4 py-2 rounded-full border" style={{ borderColor: "#E5E0D8", color: "#1E1B4B" }} data-testid="admin-category-cancel">
                            Cancel
                        </button>
                    </div>
                </form>
            )}

            <div className="mt-6 overflow-x-auto">
                {loading ? (
                    <div className="flex items-center gap-2 text-sm" style={{ color: "#525252" }}>
                        <Loader2 className="animate-spin" size={14} /> Loading…
                    </div>
                ) : (
                    <table className="w-full text-sm" data-testid="admin-category-table">
                        <thead>
                            <tr className="text-left" style={{ color: "#7F1D1D" }}>
                                <th className="py-2 pr-3 text-xs uppercase tracking-[0.18em]">#</th>
                                <th className="py-2 pr-3 text-xs uppercase tracking-[0.18em]">Slug</th>
                                <th className="py-2 pr-3 text-xs uppercase tracking-[0.18em]">EN</th>
                                <th className="py-2 pr-3 text-xs uppercase tracking-[0.18em]">TA</th>
                                <th className="py-2 pr-3 text-xs uppercase tracking-[0.18em]">FR</th>
                                <th className="py-2 pr-3 text-xs uppercase tracking-[0.18em]">Icon</th>
                                <th className="py-2 pr-3 text-xs uppercase tracking-[0.18em]">Status</th>
                                <th className="py-2 pr-3 text-xs uppercase tracking-[0.18em]">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((c) => (
                                <tr key={c.id} className="border-t" style={{ borderColor: "#E5E0D8", color: "#1E1B4B" }} data-testid={`admin-category-row-${c.slug}`}>
                                    <td className="py-2 pr-3">{c.sort_order}</td>
                                    <td className="py-2 pr-3 font-mono text-xs">{c.slug}{c.is_system && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded" style={{ background: "#F5F0E6", color: "#7F1D1D" }}>system</span>}</td>
                                    <td className="py-2 pr-3">{c.label_en}</td>
                                    <td className="py-2 pr-3">{c.label_ta || <span style={{ color: "#A3A3A3" }}>—</span>}</td>
                                    <td className="py-2 pr-3">{c.label_fr || <span style={{ color: "#A3A3A3" }}>—</span>}</td>
                                    <td className="py-2 pr-3 font-mono text-xs">{c.icon}</td>
                                    <td className="py-2 pr-3">
                                        <button onClick={() => toggleActive(c)}
                                            className="text-xs px-2 py-1 rounded-full border"
                                            style={{ borderColor: c.is_active ? "#D4AF37" : "#E5E0D8", color: c.is_active ? "#1E1B4B" : "#A3A3A3" }}
                                            data-testid={`admin-category-toggle-${c.slug}`}>
                                            {c.is_active ? "Active" : "Inactive"}
                                        </button>
                                    </td>
                                    <td className="py-2 pr-3">
                                        <div className="flex gap-1.5">
                                            <button onClick={() => startEdit(c)} className="text-xs p-1.5 rounded-full border" style={{ borderColor: "#E5E0D8", color: "#1E1B4B" }} title="Edit" data-testid={`admin-category-edit-${c.slug}`}>
                                                <Edit2 size={12} />
                                            </button>
                                            {!c.is_system && (
                                                <button onClick={() => remove(c)} className="text-xs p-1.5 rounded-full border" style={{ borderColor: "#E5E0D8", color: "#7F1D1D" }} title="Delete" data-testid={`admin-category-delete-${c.slug}`}>
                                                    <Trash2 size={12} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
